import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BookOpen, Copy, PlayCircle, Info, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LibraryTab = () => {
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [showDeployDialog, setShowDeployDialog] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [selectedBroker, setSelectedBroker] = useState('');
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [detailsStrategy, setDetailsStrategy] = useState(null);

  useEffect(() => {
    fetchLibrary();
    fetchBrokers();
  }, []);

  const fetchLibrary = async () => {
    try {
      const response = await axios.get(`${API}/library`, { withCredentials: true });
      setStrategies(response.data);
    } catch (error) {
      console.error('Failed to fetch library:', error);
    }
  };

  const fetchBrokers = async () => {
    try {
      const response = await axios.get(`${API}/brokers`, { withCredentials: true });
      setBrokers(response.data);
    } catch (error) {
      console.error('Failed to fetch brokers:', error);
    }
  };

  const duplicateStrategy = async (strategyId) => {
    try {
      await axios.post(`${API}/strategies/${strategyId}/duplicate`, {}, { withCredentials: true });
      toast.success('Strategy copied to your strategies!');
    } catch (error) {
      toast.error('Failed to copy strategy');
    }
  };

  const openDeployDialog = (strategy) => {
    if (brokers.length === 0) {
      toast.error('Please connect a broker first in the Brokers tab');
      return;
    }
    setSelectedStrategy(strategy);
    setShowDeployDialog(true);
  };

  const deployStrategy = async () => {
    if (!selectedBroker) {
      toast.error('Please select a broker');
      return;
    }

    try {
      // First copy the strategy to user's account
      const copyResponse = await axios.post(`${API}/strategies/${selectedStrategy.id}/duplicate`, {}, { withCredentials: true });
      const copiedStrategy = copyResponse.data;

      // Then deploy it
      await axios.post(`${API}/deploy`, {
        strategy_id: copiedStrategy.id,
        broker_id: selectedBroker
      }, { withCredentials: true });

      toast.success('Strategy deployed successfully!');
      setShowDeployDialog(false);
      setSelectedBroker('');
      setSelectedStrategy(null);
    } catch (error) {
      toast.error('Failed to deploy strategy');
    }
  };

  const viewDetails = (strategyId) => {
    navigate(`/strategy/${strategyId}`);
  };

  return (
    <Card className="p-8 bg-white/80 backdrop-blur-sm border-gray-200" data-testid="library-tab-content">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Strategy Library
        </h2>
        <div className="text-sm text-gray-600">
          {strategies.length} strategies available
        </div>
      </div>

      {strategies.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">No public strategies available</p>
          <p className="text-sm text-gray-400">Check back later for community strategies</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {strategies.map((strategy) => (
            <div 
              key={strategy.id} 
              className="p-6 bg-gradient-to-br from-white to-slate-50 rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-lg transition-all cursor-pointer"
              onClick={() => viewDetails(strategy.id)}
              data-testid={`strategy-card-${strategy.id}`}
            >
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-semibold text-gray-900">{strategy.name}</h3>
                <ArrowRight className="w-5 h-5 text-indigo-600" />
              </div>
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{strategy.description || 'No description'}</p>
              
              {/* Strategy Info */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Capital Required</span>
                  <span className="font-semibold text-gray-900">₹{(strategy.advanced_settings?.capital_required || 50000).toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Check Interval</span>
                  <span className="font-medium text-gray-700">{strategy.advanced_settings?.check_conditions_every || '1 minute'}</span>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeployDialog(strategy);
                  }}
                  size="sm"
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                  data-testid={`deploy-strategy-${strategy.id}`}
                >
                  <PlayCircle className="w-4 h-4 mr-2" />
                  Deploy
                </Button>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    duplicateStrategy(strategy.id);
                  }}
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  data-testid={`copy-strategy-${strategy.id}`}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Deploy Dialog */}
      <Dialog open={showDeployDialog} onOpenChange={setShowDeployDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deploy Strategy</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">{selectedStrategy?.name}</h4>
              <p className="text-sm text-gray-600">{selectedStrategy?.description}</p>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Select Broker</label>
              <Select value={selectedBroker} onValueChange={setSelectedBroker}>
                <SelectTrigger data-testid="deploy-broker-select">
                  <SelectValue placeholder="Choose broker" />
                </SelectTrigger>
                <SelectContent>
                  {brokers.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.broker_name} {b.is_connected ? '✓' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                This strategy will be copied to your account and deployed to the selected broker.
              </p>
            </div>
            <Button onClick={deployStrategy} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-deploy-library">
              Deploy Strategy
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </Card>
  );
};

export default LibraryTab;
